/**
 * PersonalEventModal.jsx — Modale d'ajout/édition d'un événement personnel.
 * Utilisée côté élève (/eleve/planning) ET côté enseignant (/moniteur/planning).
 */
import React, { useState, useEffect } from 'react';
import { X, Save, Trash2 } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const SUBJECTS = [
  { value: 'plateau', label: 'Plateau' },
  { value: 'circulation', label: 'Circulation' },
  { value: 'code', label: 'Code / Révision' },
  { value: 'salle', label: 'Cours en salle' },
  { value: 'autre', label: 'Autre' },
];

export default function PersonalEventModal({ open, onClose, event, mode, onSaved }) {
  const isEdit = !!event?.id;
  const [form, setForm] = useState({
    date: '', start_time: '', duration_min: 60,
    title: '', subject: 'plateau', contact_name: '', contact_email: '',
    location: '', notes: '', bilan_text: '', next_topics: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setForm({
        date: event?.date || today,
        start_time: event?.start_time || '',
        duration_min: event?.duration_min || 60,
        title: event?.title || '',
        subject: event?.subject || 'plateau',
        contact_name: event?.contact_name || '',
        contact_email: event?.contact_email || '',
        location: event?.location || '',
        notes: event?.notes || '',
        bilan_text: event?.bilan_text || '',
        next_topics: event?.next_topics || '',
      });
    }
  }, [open, event]);

  if (!open) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Un titre est requis'); return; }
    if (!form.date) { toast.error('Une date est requise'); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_min: Number(form.duration_min) || 60 };
      const saved = isEdit ? await api.persoUpdateEvent(event.id, payload) : await api.persoCreateEvent(payload);
      toast.success(isEdit ? 'Modifié' : 'Ajouté à ton planning');
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm('Supprimer définitivement cet événement ?')) return;
    setSaving(true);
    try {
      await api.persoDeleteEvent(event.id);
      toast.success('Supprimé');
      onSaved?.(null);
      onClose();
    } catch (e) { toast.error(e.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const isTeacher = mode === 'teacher';

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="perso-event-modal">
      <div className="w-full sm:max-w-lg bg-[#0d0d0e] border border-white/10 rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">Planning perso</div>
            <h2 className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {isEdit ? 'Modifier' : 'Ajouter'} un cours
            </h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="perso-modal-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Date</span>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-date" />
            </label>
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Heure</span>
              <input type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-time" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Type</span>
            <select value={form.subject} onChange={e => set('subject', e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-subject">
              {SUBJECTS.map(s => <option key={s.value} value={s.value} className="bg-[#0d0d0e]">{s.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Titre / Résumé *</span>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)} placeholder={isTeacher ? 'Ex : Révision plateau exercice 2' : 'Ex : Mon cours de plateau'} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-title" />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-white/60">
                {isTeacher ? 'Élève' : 'Enseignant / auto-école'} <span className="text-white/30 normal-case font-normal">(facultatif)</span>
              </span>
              <input type="text" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder={isTeacher ? 'Nom / prénom' : 'Ex : Auto-école Llorens'} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-contact" />
            </label>
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Durée (min)</span>
              <input type="number" min={15} max={480} step={15} value={form.duration_min} onChange={e => set('duration_min', e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Lieu <span className="text-white/30 normal-case font-normal">(facultatif)</span></span>
            <input type="text" value={form.location} onChange={e => set('location', e.target.value)} placeholder="Ex : Piste Jallieu" className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Notes perso <span className="text-white/30 normal-case font-normal">(facultatif)</span></span>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
          </label>

          {isTeacher && (
            <>
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-2">Bilan de fin de cours (partagé par QR)</div>
                <label className="block">
                  <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Ce qu&apos;on a vu / bilan</span>
                  <textarea value={form.bilan_text} onChange={e => set('bilan_text', e.target.value)} rows={3} placeholder="Ex : Slalom rapide OK, virage serré à revoir…" className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-bilan" />
                </label>
                <label className="block mt-3">
                  <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Prochains cours à voir</span>
                  <textarea value={form.next_topics} onChange={e => set('next_topics', e.target.value)} rows={2} placeholder="Ex : Exercice évitement + freinage d'urgence" className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="perso-input-next" />
                </label>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex items-center gap-2">
          {isEdit && (
            <button onClick={remove} disabled={saving} className="px-3 py-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="perso-btn-delete">
              <Trash2 size={13} /> Supprimer
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/15 text-white/75 hover:bg-white/5 text-sm">Annuler</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-black inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="perso-btn-save">
            <Save size={13} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
